import axios from 'axios';
import { getItem, setItem } from './storage.handler';

const PAGE_SIZE = 25;

const apiUrl = process.env.REACT_APP_API_URL;
//#region Authentication

/** Authentication header */

const token = window.localStorage.getItem('token');
axios.defaults.headers = {};
const pathNames = [
    'login',
    'about',
    'contact',
    'properties',
    'property',
    'services',
    'service',
    'privacy',
    'become-tenant',
    'reset-password',
    '',
];
const path = window.location.pathname.split('/')[1];

if (!token && path !== '/') {
    if (!pathNames.includes(path)) {
        window.location.replace('/');
    }
} else {
    axios.defaults.headers = {
        'Content-Type': 'application/json',
        Authorization: `Token ${token}`,
    };
}

const getCache = (cacheKey) => getItem(cacheKey);
const setCache = (cacheKey, data) => setItem(cacheKey, data);

const fetchSpecificPage = async (type, page, searchQuery = '') => {
    const url = searchQuery
        ? `${apiUrl}${type}/search/`
        : `${apiUrl}${type}/`;

    try {
        const response = await axios.get(url, {
            params: { q: searchQuery, page, page_size: PAGE_SIZE },
        });

        const { results, next, previous, count, total } = response.data;
        return { results, next, previous, count, total };
    } catch (error) {
        handleError(error, `Error fetching ${type} for specific page`);
        return { results: [], next: null, previous: null, count: 0 };
    }
};

export const getItemsForDropdown = async (type, id = null) => {
    const CACHE_KEY = type; // **Same cache key for all queries**
    try {
        const response = await axios.get(`${apiUrl}${type}/`, {
            params: { id: id, page: 1, page_size: PAGE_SIZE },
        });

        const { results, next, previous, count } = response.data;

        // Replace existing cache instead of creating new keys
        const newCacheData = { results, next, previous, count };
        setCache(CACHE_KEY, newCacheData);

        return newCacheData;
    } catch (error) {
        handleError(error, `Error fetching ${type}`);
        return { results: [], next: null, previous: null, count: 0 };
    }
};

export const getItems = async (type, firstLoad, page = 1, searchQuery = '', reset = true) => {
    const CACHE_KEY = type;

    // Handle search query
    if (searchQuery) {
        const newData = await fetchSpecificPage(type, page, searchQuery);
        setCache(CACHE_KEY, newData);
        return newData;
    }

    // Handle pagination
    if (page !== 1) {
        return fetchSpecificPage(type, page);
    }

    const cachedData = getCache(CACHE_KEY);

    // If it's the first load and we have cache, return it
    if (firstLoad && cachedData && !reset) {
        return cachedData;
    }

    // If not resetting and cache exists, check if we can extend it
    if (!reset && cachedData) {
        const { results, next } = cachedData;

        if (next) {
            try {
                const response = await axios.get(next);
                const {
                    results: newResults,
                    next: newNext,
                    previous: newPrevious,
                    count: newCount,
                } = response.data;

                const updatedCache = {
                    ...cachedData,
                    results: [...results, ...newResults],
                    next: newNext,
                    previous: newPrevious,
                    count: newCount,
                };

                setCache(CACHE_KEY, updatedCache);
                return updatedCache;
            } catch (error) {
                handleError(error, `Error fetching ${type} from next page`);
                return { results: [], next: null, previous: null, count: 0 };
            }
        }

        return cachedData;
    }

    // Otherwise, fetch fresh data (either cache is empty or reset is true)
    try {
        const response = await axios.get(`${apiUrl}${type}/`, {
            params: { page: 1, page_size: PAGE_SIZE },
        });

        const { results, next, previous, count, total } = response.data;
        const newCacheData = { results, next, previous, count, total };
        setCache(CACHE_KEY, newCacheData);
        return newCacheData;
    } catch (error) {
        handleError(error, `Error fetching ${type}`);
        return { results: [], next: null, previous: null, count: 0 };
    }
};

export const getItemById = async (type, id) => {
    let item = null;

    // Get all cached data for the type (e.g., companies, leads)
    const cachedData = getCache(type);

    if (cachedData) {
        // Search for the item in the cached results
        item = cachedData.results.find((item) => item.id === id);
    }

    if (!item) {
        // If the item is not found in cache, make an API call to fetch it
        try {
            const response = await axios.get(`${apiUrl}${type}/${id}/`);
            item = response.data;
        } catch (error) {
            handleError(error, `Error fetching ${type} with id ${id}`);
            item = null;
        }
    }

    return item;
};

export const updateItem = async (type, data) => {
    try {
        const response = await axios.put(`${apiUrl}${type}/${data.id}/`, data);
        return response.data;
    } catch (error) {
        handleError(error, `Error updating ${type}`);
        return null;
    }
};

export const createItem = async (type, data) => {
    try {
        const response = await axios.post(`${apiUrl}${type}/`, data);
        return response.data;
    } catch (error) {
        handleError(error, `Error creating ${type}`);
        return null;
    }
};

export const deleteItem = async (type, id) => {
    try {
        const response = await axios.delete(`${apiUrl}${type}/${id}`);
        return response.data;
    } catch (error) {
        handleError(error, `Error deleting ${type} with id ${id}`);
        return null;
    }
};

/**
 * Submit an application (or any form that includes files).
 *
 * @param {string} type          – key that maps to endpoints[type]
 * @param {Object} formObj       – plain JS object containing fields + a `documents` field
 *                                 that can be File, FileList, or File[]
 * @returns {Object|null}        – response data on success, or null on failure
 */
export const submitMultipart = async (formObj) => {

    // ---------- Build FormData -------------------------------------------------
    const fd = new FormData();

    // add all scalar / text fields first
    Object.entries(formObj).forEach(([key, value]) => {
        if (key === 'documents') return; // skip file field for now
        fd.append(key, value ?? '');
    });

    // append documents (File, FileList, or array of Files)
    const { documents } = formObj;
    if (documents) {
        if (documents instanceof FileList || Array.isArray(documents)) {
            Array.from(documents).forEach((file) => fd.append('documents', file));
        } else if (documents instanceof File) {
            fd.append('documents', documents);
        }
    }

    // ---------- Axios call -----------------------------------------------------
    try {
        const { data } = await axios.post(`${apiUrl}application`, fd, {
            headers: {
                'Content-Type': 'multipart/form-data',
                // Authorization: `Token ${localStorage.getItem('token')}`, // uncomment if needed
            },
        });
        return data;
    } catch (error) {
        handleError(error, 'Error submitting application form.');
        return null;
    }
};

//#endregion

// #region Error handler

let setModalStateGlobal = () => {}; // Default empty function

export const setRecoilModalSetter = (setter) => {
    setModalStateGlobal = setter;
};

export const handleError = (
    error,
    customMessage = 'An error occurred while processing your request.'
) => {
    let errorMessage;

    const data = error?.response?.data;

    if (data?.message) {
        errorMessage = data.message;
    } else if (data?.detail) {
        errorMessage = data.detail;
    } else if (data?.errors) {
        const firstField = Object.keys(data.errors)[0];
        errorMessage = data.errors[firstField]?.[0];
    } else if (error?.message) {
        errorMessage = error.message;
    } else {
        errorMessage = customMessage;
    }

    setModalStateGlobal({
        isOpen: true,
        message: errorMessage || customMessage,
    });
};


// #endregion